import React, { useCallback } from "react";
import { graphql } from "react-relay";

import { faAddressCard, faBuilding, faFileInvoice, faSuitcase } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fetchQuery } from "relay-runtime";

import { showFeatureModal } from "~/billing";
import { billingUpgradeIconDef } from "~/billing/utils";
import { environment } from "~/models";
import HubspotSendNotesModal from "~/stream/SideBar/CRMPanel/Hubspot/HubspotSendNotes";

import HubspotPanel, { CRMItem, type HubspotInnerPaneProps } from "./HubspotPanel";

import type { HubspotQuery, HubspotQuery$data } from "~/__generated__/HubspotQuery.graphql";
import type { HubspotSendNotesAutomationStatusQuery } from "~/__generated__/HubspotSendNotesAutomationStatusQuery.graphql";

import styles from "./css/HubspotPanelWrapper.module.css";

export type HubspotPanelNode = NonNullable<HubspotQuery$data["hubspotContactsForStream"]>[number];

export const hubspotQuery = graphql`
	query HubspotQuery($streamId: ID!) {
		hubspotContactsForStream(id: $streamId) {
			__typename
			id
			url
			name
			email
			title
			company {
				id
				url
				name
			}
			deals {
				id
				url
				amount
				name
			}
			tickets {
				id
				url
				subject
				description
			}
		}
	}
`;

const hubspotSendNotesAutomationStatusQuery = graphql`
	query HubspotSendNotesAutomationStatusQuery($noteId: ID!) {
		note: node(id: $noteId) {
			... on Note {
				sendNoteToCrmAutomationIsValid
				sendNoteAutomationIsActive: sendNoteToHubspotAutomationIsActive
			}
		}
	}
`;

interface PrivateProps {
	streamId: string;
	noteId: string;
	hasAccessToHubspotIntegration: boolean;
}

export function HubspotAccount({ company }: { company: NonNullable<HubspotPanelNode["company"]> }) {
	return (
		<CRMItem
			logo={<FontAwesomeIcon fixedWidth size="lg" icon={faBuilding} />}
			url={company.url}
			title={company.name}
			primary={"Company"}
			secondary={company.name}
		/>
	);
}

export function HubspotContact({ contact }: { contact: HubspotPanelNode }) {
	return (
		<CRMItem
			logo={<FontAwesomeIcon fixedWidth size="lg" icon={faAddressCard} />}
			url={contact.url}
			title={contact.name}
			primary={"Contact"}
			secondary={"title" in contact ? contact.title : null}
		/>
	);
}

export function HubspotDeal({ deal }: { deal: HubspotPanelNode["deals"][number] }) {
	return (
		<CRMItem
			logo={<FontAwesomeIcon fixedWidth size="lg" icon={faFileInvoice} />}
			url={deal.url}
			title={deal.name}
			primary={"Deal"}
			secondary={deal.amount}
		/>
	);
}

export function HubspotTicket({ ticket }: { ticket: HubspotPanelNode["tickets"][number] }) {
	return (
		<CRMItem
			logo={<FontAwesomeIcon fixedWidth size="lg" icon={faSuitcase} />}
			url={ticket.url}
			title={ticket.subject || ticket.description || "Ticket"}
			primary={"Ticket"}
			secondary={ticket.description ?? ""}
		/>
	);
}

function HubspotInnerPane({ activeNode: contact }: HubspotInnerPaneProps<HubspotPanelNode>) {
	return (
		<div>
			<HubspotContact contact={contact} />
			{"company" in contact && contact.company !== null ? <HubspotAccount company={contact.company} /> : false}
			{"deals" in contact ? contact["deals"].map(deal => <HubspotDeal key={deal.id} deal={deal} />) : false}
			{"tickets" in contact
				? contact["tickets"].map(ticket => <HubspotTicket key={ticket.id} ticket={ticket} />)
				: false}
		</div>
	);
}

export function HubspotNoneFound() {
	return <div>No HubSpot users could be found for this stream</div>;
}

const Hubspot: React.FunctionComponent<PrivateProps> = ({ streamId, noteId, hasAccessToHubspotIntegration }) => {
	const showFeature = useCallback(() => showFeatureModal("hubspot_integration"), []);

	const crmQuery = useCallback(
		(streamId: string) =>
			fetchQuery<HubspotQuery>(environment, hubspotQuery, {
				streamId,
			}),
		[],
	);

	const sendNotesAutomationQuery = useCallback(
		(noteId: string) =>
			fetchQuery<HubspotSendNotesAutomationStatusQuery>(environment, hubspotSendNotesAutomationStatusQuery, {
				noteId,
			}),
		[],
	);

	return (
		<HubspotPanel
			streamId={streamId}
			noteId={noteId}
			hasAccessToIntegration={hasAccessToHubspotIntegration}
			noneFound={<HubspotNoneFound />}
			InnerPane={HubspotInnerPane}
			Modal={HubspotSendNotesModal}
			crmQuery={crmQuery}
			sendNotesAutomationQuery={sendNotesAutomationQuery}
			SendNotesButtonContent={
				<>
					{!hasAccessToHubspotIntegration && (
						<FontAwesomeIcon icon={billingUpgradeIconDef} className={styles.paywallIcon} />
					)}
					Send note to HubSpot
				</>
			}
			showFeature={showFeature}
		/>
	);
};

export default Hubspot;
